<template>
  <div
    :class="customClasses"
    class="ellipsis-text"
  >
    <div
      ref="text"
      :class="textClasses"
      :style="clampStyle"
      class="ellipsis-text__text"
    >
      <slot>
        <div v-safe-html="text" />
      </slot>
    </div>

    <v-btn
      v-if="!hideButton && ellipsis && isTruncated"
      :aria-expanded="expanded ? 'true' : 'false'"
      :class="buttonClasses"
      class="ellipsis-text__toggle"
      v-bind="mergedButtonProps"
      @click="toggle"
      v-on="buttonListeners"
    >
      <slot
        :expanded="expanded"
        :toggle="toggle"
        name="button"
      >
        {{ expanded ? lessLabel : moreLabel }}
      </slot>
    </v-btn>
  </div>
</template>

<script>
export default {
  name: 'EllipsisText',

  props: {
    /** Text to display (the default slot can be used instead). */
    text: {
      type   : String,
      default: ''
    },
    /** Extra classes for the root element: string, array or object. */
    customClasses: {
      type   : [String, Array, Object],
      default: ''
    },
    /** Extra classes for the text element. */
    textClasses: {
      type   : [String, Array, Object],
      default: ''
    },
    /** Extra classes for the toggle button. */
    buttonClasses: {
      type   : [String, Array, Object],
      default: ''
    },
    /**
     * Any Vuetify v2 <v-btn> props (color, text, small, outlined, icon,
     * depressed, rounded, ripple, to, href, disabled, loading, ...).
     * Merged over the defaults below.
     */
    buttonProps: {
      type   : Object,
      default: () => ({})
    },
    /** Extra listeners for the <v-btn> (the click toggle always runs). */
    buttonListeners: {
      type   : Object,
      default: () => ({})
    },
    /** Turns truncation on/off. */
    ellipsis: {
      type   : Boolean,
      default: false
    },
    /** shows or hides the button */
    hideButton: {
      type   : Boolean,
      default: false
    },
    /**
     * Number of visible lines.
     * 1 = single-line ellipsis (text-overflow), >1 = -webkit-line-clamp.
     */
    lines: {
      type     : Number,
      default  : 1,
      validator: (value) => Number.isInteger(value) && value > 0
    },
    moreLabel: {
      type   : String,
      default: function () {
        return this.$t('Buttons.More')
      }
    },
    lessLabel: {
      type   : String,
      default: function () {
        return this.$t('Buttons.Less')
      }
    }
  },

  data () {
    return {
      expanded   : false,
      isTruncated: false
    }
  },

  computed: {
    mergedButtonProps () {
      return {
        small    : true,
        depressed: true,
        color    : 'primary',
        ...this.buttonProps
      }
    },

    isClamped () {
      return this.ellipsis && !this.expanded
    },

    // Inline styles so build tools (autoprefixer) can't strip -webkit-box-orient.
    clampStyle () {
      if (!this.isClamped) return null

      if (this.lines === 1) {
        return {
          overflow    : 'hidden',
          whiteSpace  : 'nowrap',
          textOverflow: 'ellipsis'
        }
      }

      return {
        overflow       : 'hidden',
        display        : '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: String(this.lines),
        lineClamp      : String(this.lines)
      }
    }
  },

  watch: {
    text    : 'reset',
    lines   : 'reset',
    ellipsis: 'reset'
  },

  mounted () {
    this.checkTruncation()

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.checkTruncation())
      this.resizeObserver.observe(this.$refs.text)
    } else {
      window.addEventListener('resize', this.checkTruncation)
    }
  },

  updated () {
    // Catches slot content changes.
    this.checkTruncation()
  },

  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    } else {
      window.removeEventListener('resize', this.checkTruncation)
    }
  },

  methods: {
    toggle () {
      this.expanded = !this.expanded
      this.$emit('toggle', this.expanded)
    },

    reset () {
      this.expanded = false
      this.$nextTick(this.checkTruncation)
    },

    checkTruncation () {
      // While expanded the text isn't clamped, so keep the button visible.
      if (this.expanded) return

      const el = this.$refs.text
      if (!el || !this.ellipsis) {
        this.isTruncated = false
        return
      }

      // 1px tolerance for sub-pixel rounding.
      const truncated =
        this.lines === 1
          ? el.scrollWidth - el.clientWidth > 1
          : el.scrollHeight - el.clientHeight > 1

      if (truncated !== this.isTruncated) {
        this.isTruncated = truncated
      }
    }
  }
}
</script>

<style scoped>
.ellipsis-text__text {
  word-break : break-word;
}

.ellipsis-text__toggle {
  margin-top : 4px;
}
</style>
